<template>
	<div>
		<RouletteBlock
			:items="rouletteItems"
			:count="count"
			:price="price"
			:fast="fast"
			class="mb-5 scroll-m-15"
			ref="rouletteBlock"
		>
			<template #footer>
				<slot name="overlay">
					<div
						class="absolute inset-0 flex items-center justify-center"
						v-if="loading"
					>
						<LoaderIcon class="text-separator-secondary h-10 w-10" />
					</div>
				</slot>
			</template>
		</RouletteBlock>
		<div
			class="bg-fill-tertiary -mt-10 mb-6 flex w-full items-center justify-between gap-4 rounded-b-xl px-4 pt-8 pb-4"
		>
			<slot name="hint">
				<div class="w-full" v-if="!bottomActions">
					<div class="mb-3 flex w-full items-center gap-1.5" v-if="price">
						<TagGroup
							v-model="selectedCount"
							:tags="[
								{ text: '1' },
								{ text: '2' },
								{ text: '3' },
								{ text: '4' }
							]"
							class="flex-1 *:flex-1"
							:disabled="rolling"
						/>
						<div class="bg-separator-secondary mx-2 h-4 w-px"></div>
						<BaseButton
							square
							size="md"
							color="secondary"
							@click="toggleFast()"
							:disabled="rolling"
							:class="{
								'border-yellow outline-fill-tertiary border-2 outline-2 -outline-offset-5':
									fast
							}"
						>
							<LightningFillIcon class="text-yellow h-5 w-5" v-if="fast" />
							<LightningOutlineIcon class="h-5 w-5" v-else />
						</BaseButton>
					</div>
					<div class="flex w-full gap-3">
						<BaseButton
							class="flex-1"
							size="lg"
							color="primary"
							:loading="rolling"
							@click="open()"
							v-if="price"
						>
							<div>{{ t('openFor', [count]) }}</div>
							<CoinPrice class="ml-1.5" :price="price * count" />
						</BaseButton>
						<BaseButton
							class="flex-1"
							size="lg"
							:color="
								droppedItems.length ||
								locked ||
								(type === 'referral' &&
									cases.referralCase &&
									!cases.referralCase.canOpen)
									? 'secondary'
									: 'primary'
							"
							:loading="rolling"
							:disabled="
								droppedItems.length > 0 ||
								locked ||
								(type === 'referral' &&
									cases.referralCase &&
									!cases.referralCase.canOpen)
							"
							@click="open()"
							v-if="!price && (type === 'reward' || type === 'referral')"
						>
							<div>
								{{ openRewardText }}
							</div>
						</BaseButton>
						<BaseButton
							class="flex-1"
							size="lg"
							:color="!cases.welcomeCase?.count ? 'secondary' : 'primary'"
							:loading="rolling"
							:disabled="!cases.welcomeCase?.count"
							@click="open()"
							v-if="!price && type === 'welcome'"
						>
							<div>
								{{ openWelcomeText }}
							</div>
						</BaseButton>
					</div>
				</div>
			</slot>
		</div>
		<slot name="live" />
		<PagePadding class="mb-2 flex items-center justify-between px-3">
			<SubtitleText>{{ t('contains') }}</SubtitleText>
			<SubtitleText v-if="versionCreatedAt" class="font-normal!">
				<AgoText :prefix="t('updated')" :date="versionCreatedAt" />
			</SubtitleText>
		</PagePadding>
		<PagePadding
			class="grid grid-cols-2 gap-1.5 min-[480px]:grid-cols-3 min-[720px]:grid-cols-4 min-[960px]:grid-cols-5"
		>
			<ItemCard
				v-for="item in items"
				:src="icon(item.modelUuid)"
				:preloader="item.preloader || undefined"
				:collection="item.collectionName"
				:name="item.modelName"
				:price="item.price"
				:chance="item.chance"
				:color="getItemColor(item, price)"
				@click="selectItem(item)"
			/>
		</PagePadding>
		<BottomActions v-if="bottomActions">
			<div class="mb-3 flex w-full items-center gap-1.5" v-if="price">
				<TagGroup
					v-model="selectedCount"
					:tags="[{ text: '1' }, { text: '2' }, { text: '3' }, { text: '4' }]"
					class="flex-1 *:flex-1"
					:disabled="rolling"
				/>
				<div class="bg-separator-secondary mx-2 h-4 w-px"></div>
				<BaseButton
					square
					size="md"
					color="secondary"
					@click="toggleFast()"
					:disabled="rolling"
				>
					<LightningFillIcon class="text-yellow h-5 w-5" v-if="fast" />
					<LightningOutlineIcon class="h-5 w-5" v-else />
				</BaseButton>
			</div>
			<div class="flex w-full gap-3">
				<slot name="actionReplace">
					<slot name="actionLeft" />
					<BaseButton
						class="flex-1"
						size="lg"
						color="primary"
						:loading="rolling"
						@click="open()"
						v-if="price"
					>
						<div>{{ t('openFor', [count]) }}</div>
						<CoinPrice class="ml-1.5" :price="price * count" />
					</BaseButton>
					<BaseButton
						class="flex-1"
						size="lg"
						:color="
							droppedItems.length ||
							locked ||
							(type === 'referral' &&
								cases.referralCase &&
								!cases.referralCase.canOpen)
								? 'secondary'
								: 'primary'
						"
						:loading="rolling"
						:disabled="
							droppedItems.length > 0 ||
							locked ||
							(type === 'referral' &&
								cases.referralCase &&
								!cases.referralCase.canOpen)
						"
						@click="open()"
						v-if="!price && (type === 'reward' || type === 'referral')"
					>
						<div>
							{{ openRewardText }}
						</div>
					</BaseButton>
					<BaseButton
						class="flex-1"
						size="lg"
						:color="!cases.welcomeCase?.count ? 'secondary' : 'primary'"
						:loading="rolling"
						:disabled="!cases.welcomeCase?.count"
						@click="open()"
						v-if="!price && type === 'welcome'"
					>
						<div>
							{{ openWelcomeText }}
						</div>
					</BaseButton>
				</slot>
			</div>
		</BottomActions>
		<DropModal
			v-model="showDrop"
			:items="droppedItems"
			:cannotBeSold="type === 'reward'"
			:sharing="shareLoading"
			:casePrice="price"
			@openFairness="openFairness(true)"
			@share="share"
		/>
		<CaseFairModal
			:fair="fair"
			v-model="fairness"
			:noRotate="fair.ticket === null"
		/>
		<ItemTicketsModal
			v-if="selectedItem"
			v-model="itemModal"
			v-bind="selectedItem"
		/>
		<RenderAsImage
			:width="432"
			:height="768"
			backgroundColor="#141414"
			ref="imageRender"
		>
			<CaseShareStory
				v-if="sharing.modelUuid"
				:uuid="uuid"
				:modelUuid="sharing.modelUuid"
				:collectionName="sharing.collectionName"
				:modelName="sharing.modelName"
				:chance="sharing.chance"
				:price="sharing.price"
			/>
		</RenderAsImage>
	</div>
</template>
<script setup lang="ts">
import PagePadding from '@/components/PagePadding.vue'
import BaseButton from '@/components/BaseButton.vue'
import RiskBar from '@/components/RiskBar.vue'
import TagGroup from '@/components/TagGroup.vue'
import LightningOutlineIcon from '@/assets/icons/lightning-outline.svg'
import LightningFillIcon from '@/assets/icons/lightning-fill.svg'
import { computed, nextTick, onUnmounted, ref, useSlots, watch } from 'vue'
import { sleep } from '@/utils/sleep'
import ItemCard from '@/components/ItemCard.vue'
import DropModal from '@/components/modals/DropModal.vue'
import BottomActions from '@/components/BottomActions.vue'
import { client } from '@/api/orpc'
import { useUser } from '@/stores/user'
import SubtitleText from '@/components/SubtitleText.vue'
import { animation, icon } from '@/utils/urls'
import RouletteBlock from '@/components/RouletteBlock.vue'
import { useRouter } from 'vue-router'
import CoinPrice from '@/components/CoinPrice.vue'
import { useInventory } from '@/stores/inventory'
import { useCases } from '@/stores/cases'
import { useI18n } from 'vue-i18n'
import CaseFairModal from '@/components/modals/CaseFairModal.vue'
import ItemTicketsModal from '@/components/modals/ItemTicketsModal.vue'
import { useLayout } from '@/stores/layout'
import LoaderIcon from '@/assets/icons/loader.svg'
import ShieldIcon from '@/assets/icons/shield.svg'
import { useToast } from '@/stores/toast'
import { WebApp } from '@grammyjs/web-app'
import RenderAsImage from './RenderAsImage.vue'
import CaseShareStory from './CaseShareStory.vue'
import { getItemColor } from '@/utils/itemColor'
import AgoText from './AgoText.vue'

const { t } = useI18n({
	messages: {
		en: {
			cost: 'сost',
			demoSpin: 'demo spin',
			openFor: 'open {0} for',
			risk: {
				low: 'Low risk',
				medium: 'Medium risk',
				high: 'High risk'
			},
			contains: 'Case Contains',
			free: 'open for free',
			open: 'open',
			required: '{0} required',
			next: 'next in {0}',
			cta: `\n🎁 {0} - Open now on {'@'}ragegift ⤴️`,
			fair: 'Provably Fair',
			caseNotFound: 'Case not found',
			claim: 'Claim bonus',
			outOfStock: 'Out of stock',
			shareFailed: 'Failed to share to story',
			alreadyOpened: 'You have already opened this case',
			unexpectedError: 'An unexpected error occurred',
			maintenance: 'Case opening is currently disabled due to maintenance',
			referrals: '{0}/{1} invited',
			updated: 'Updated'
		},
		ru: {
			cost: 'цена',
			demoSpin: 'демо спин',
			openFor: 'открыть {0} за',
			risk: {
				low: 'Низкий риск',
				medium: 'Средний риск',
				high: 'Высокий риск'
			},
			contains: 'Гифты в кейсе',
			free: 'открыть бесплатно',
			open: 'открыть',
			required: 'требуется {0}',
			next: 'следующий {0}',
			cta: `\n🎁 {0} - Открой сейчас {'@'}ragegift ⤴️`,
			fair: 'Честная Игра',
			caseNotFound: 'Кейс не найден',
			claim: 'Забрать бонус',
			outOfStock: 'Закончились',
			shareFailed: 'Не удалось поделиться в историю',
			alreadyOpened: 'Ты уже открыл этот кейс',
			unexpectedError: 'Произошла непредвиденная ошибка',
			maintenance:
				'Открытие кейсов временно недоступно из-за технических работ',
			referrals: 'Приглашено {0} из {1}',
			updated: 'Обновлен'
		}
	}
})

const props = defineProps<{
	uuid: string
	cantOpen?: boolean
	bottomActions?: boolean
}>()

const emit = defineEmits<{
	(
		e: 'loaded',
		price: number,
		type: 'regular' | 'reward' | 'referral' | 'welcome',
		version: number,
		hasPreviousVersions: boolean,
		risk: number,
		topDrop: {
			collectionName: string
			modelName: string
			modelUuid: string
			preloader: string | null
			price: number
		}[]
	): void
	(e: 'open'): void
	(e: 'opened'): void
	(e: 'available', value: number): void
	(e: 'conditionsNotMet'): void
}>()

const selectedCount = ref(0)
const count = computed(() => selectedCount.value + 1)

const fast = ref(false)
const toggleFast = () => {
	fast.value = !fast.value
}

const type = ref<'regular' | 'reward' | 'referral' | 'welcome'>('regular')
const locked = ref(false)
const name = defineModel<string>('name', { default: '' })
const price = defineModel<number>('price', { default: 0 })
const risk = ref(0)
const items = ref<
	{
		modelUuid: string
		price: number
		modelName: string
		collectionName: string
		chance: number
		preloader: string | null
		ticketsRange: [number, number]
	}[]
>([])
const version = ref<number>(1)
const versionCreatedAt = ref<Date>()

const rouletteItems = computed<[string, string | undefined | null, number][]>(
	() =>
		items.value.map((item) => [
			icon(item.modelUuid),
			item.preloader || undefined,
			item.price
		])
)

const droppedItems = defineModel<
	{
		uuid: string
		src: string
		modelUuid: string
		collection: string
		name: string
		price: number
		chance: number
		preloader: string | null
		fair: {
			nonce: number
			clientSeed: string
			serverSeed: string | null
			serverSeedHash: string
			ticketsCount: number
			ticket: number
		}
		tickets: [number, number]
		claimable: boolean
	}[]
>('droppedItems', { default: [] })
const showDrop = ref(false)

let timeoutId: ReturnType<typeof setTimeout>

const resetReward = async () => {
	await sleep(1000)
	droppedItems.value = []
	showDrop.value = false
	await getCase()
}

const router = useRouter()
const cases = useCases()

const loaded = (
	price: number,
	type: 'regular' | 'reward' | 'referral' | 'welcome',
	version: number,
	hasPreviousVersions: boolean,
	risk: number,
	topDrop: {
		collectionName: string
		modelName: string
		modelUuid: string
		preloader: string | null
		price: number
	}[]
) => {
	emit('loaded', price, type, version, hasPreviousVersions, risk, topDrop)
}

const loading = ref(true)

const getCase = async () => {
	loading.value = true

	const { error, data, isDefined } = await client.game.getCaseItems({
		uuid: props.uuid
	})

	if (isDefined) {
		if (error.code === 'CASE_NOT_FOUND') {
			toast.add({
				type: 'error',
				text: t('caseNotFound')
			})
			router.replace('/')
		}

		return
	}

	if (error) {
		router.replace('/')
		console.error(error)
		return
	}

	if (!data) {
		return
	}

	if (!cases.rewards.length && data.type === 'reward') {
		await cases.getRewardCases()
	}

	if (data.type === 'regular') {
		cases.setRegularCase(data.uuid, data.price, data.version)

		versionCreatedAt.value = data.versionCreatedAt
			? new Date(data.versionCreatedAt)
			: undefined
		version.value = data.version
		price.value = data.price
		risk.value = data.risk
	}

	name.value = data.name
	items.value = data.items
	type.value = data.type
	locked.value = data.type === 'reward' ? data.isLocked : false

	if (data.type === 'reward' && data.wonItem) {
		droppedItems.value = [
			{
				uuid: '',
				src: animation(data.wonItem.modelUuid),
				modelUuid: data.wonItem.modelUuid,
				collection: data.wonItem.collectionName,
				name: data.wonItem.modelName,
				price: data.wonItem.price,
				chance: data.wonItem.chance,
				preloader: data.wonItem.preloader,
				fair: data.wonItem.fair,
				tickets: data.wonItem.ticketsRange,
				claimable: true
			}
		]

		showDrop.value = true
	}
	if (data.type === 'referral' && data.wonItem) {
		droppedItems.value = [
			{
				uuid: data.wonItem.isInInventory ? data.wonItem.uuid : '',
				src: animation(data.wonItem.modelUuid),
				modelUuid: data.wonItem.modelUuid,
				collection: data.wonItem.collectionName,
				name: data.wonItem.modelName,
				price: data.wonItem.price,
				chance: data.wonItem.chance,
				preloader: data.wonItem.preloader,
				fair: data.wonItem.fair,
				tickets: data.wonItem.ticketsRange,
				claimable: data.wonItem.isInInventory
			}
		]

		showDrop.value = true
	}

	if (data.type === 'reward' && data.wonItem) {
		if (cases.rewardsLockedUntil) {
			const timeUntilUnlock = cases.rewardsLockedUntil.getTime() - Date.now()

			if (timeoutId) {
				clearTimeout(timeoutId)
			}

			timeoutId = setTimeout(() => resetReward(), timeUntilUnlock)
		}
	}

	await nextTick()

	loaded(
		price.value,
		type.value,
		version.value,
		data.type === 'regular' ? data.hasPreviousVersions : false,
		risk.value,
		data.type === 'regular' && data.topDrops ? data.topDrops : []
	)

	loading.value = false
}

const user = useUser()

watch(
	() => user.token,
	() => {
		if (!user.token) {
			return
		}

		getCase()
	},
	{ immediate: true }
)

const rouletteBlock = ref<InstanceType<typeof RouletteBlock>>()

const rolling = defineModel<boolean>('rolling', { default: false })

const spin = async (
	items: {
		src: string
		imgSrc: string
		preloader?: string | null
		price: number
		chance: number
	}[]
) => {
	if (!items.length) {
		return
	}
	if (!rouletteBlock.value) {
		return
	}

	try {
		await rouletteBlock.value.spin(items)
	} catch (e) {
		console.error(e)
	}
}

const inventory = useInventory()
const layout = useLayout()

const toast = useToast()

const open = async () => {
	emit('open')

	if (props.cantOpen) {
		return
	}
	if (rolling.value) {
		return
	}
	if (user.displayedCoins < price.value * count.value) {
		return layout.showDepositModal(
			Math.ceil(price.value * count.value - user.displayedCoins)
		)
	}

	rolling.value = true

	const { error, data, isDefined } = await client.game.openCase({
		uuid: props.uuid,
		count: count.value,
		clientSeed: user.clientSeed,
		version: version.value
	})

	if (isDefined) {
		if (error.code === 'MAINTENANCE') {
			toast.add({ type: 'info', text: t('maintenance') })
		}
		if (error.code === 'CASE_NOT_FOUND') {
			toast.add({ type: 'info', text: t('caseNotFound') })
			router.replace('/')
		}
		if (error.code === 'NOT_ENOUGH_COINS') {
			layout.showDepositModal(
				Math.ceil(price.value * count.value - user.displayedCoins)
			)
		}
		if (
			error.code === 'WELCOME_CONDITIONS_NOT_MET' ||
			error.code === 'REFERRAL_CONDITIONS_NOT_MET'
		) {
			emit('conditionsNotMet')
		}
		if (error.code === 'CASE_ALREADY_OPENED') {
			toast.add({ type: 'error', text: t('alreadyOpened') })
			getCase()
		}

		rolling.value = false

		return
	}

	if (error) {
		console.error(error)
		toast.add({ type: 'error', text: t('unexpectedError') })
		rolling.value = false
		return
	}

	if (!data) {
		rolling.value = false
		return
	}

	if (data.type === 'welcome' && cases.welcomeCase) {
		cases.setWelcomeCaseCount(cases.welcomeCase.count - 1)
	}

	if (data.type === 'regular') {
		user.setUpdated(
			data.updatedUser.coins,
			data.updatedUser.rewards.level,
			data.updatedUser.rewards.xp,
			data.updatedUser.rewards.levelExpSize,
			data.updatedUser.rewards.levelPercent
		)
	}

	if (data.type === 'reward') {
		cases.setRewardCase(props.uuid, data.wonItems[0])
		cases.setRewardCasesCount(cases.rewardCasesCount - 1)
	}

	if (data.type === 'referral') {
		cases.setReferralOpen(false)
	}

	await spin(
		data.wonItems.map((item) => ({
			src: animation(item.modelUuid),
			imgSrc: icon(item.modelUuid),
			preloader: item.preloader || undefined,
			price: item.price,
			chance: item.chance
		}))
	)

	emit('opened')

	if (
		data.type === 'welcome' ||
		data.type === 'regular' ||
		data.type === 'referral'
	) {
		inventory.pushItems(data.wonItems)

		droppedItems.value = data.wonItems.map((item) => ({
			uuid: item.uuid,
			src: animation(item.modelUuid),
			modelUuid: item.modelUuid,
			collection: item.collectionName,
			name: item.modelName,
			price: item.price,
			chance: item.chance,
			preloader: item.preloader,
			fair: item.fair,
			tickets: item.ticketsRange,
			claimable: item.claimable
		}))
	}

	if (data.type === 'reward') {
		user.setBalance(data.updatedUser.coins)

		droppedItems.value = data.wonItems.map((item) => ({
			uuid: '',
			src: animation(item.modelUuid),
			modelUuid: item.modelUuid,
			collection: item.collectionName,
			name: item.modelName,
			price: item.price,
			chance: item.chance,
			preloader: item.preloader,
			fair: item.fair,
			tickets: item.ticketsRange,
			claimable: item.claimable
		}))

		if (cases.rewardsLockedUntil) {
			const timeUntilUnlock = cases.rewardsLockedUntil.getTime() - Date.now()

			if (timeoutId) {
				clearTimeout(timeoutId)
			}

			timeoutId = setTimeout(() => resetReward(), timeUntilUnlock)
		}
	}

	user.seedPairGames += data.wonItems.length

	showDrop.value = true

	rolling.value = false
}

const slots = useSlots()

const openRewardText = computed(() => {
	if (droppedItems.value.length) {
		return t('next', [cases.lockedUntil])
	}
	if (locked.value) {
		return t('required', [name.value])
	}
	if (
		type.value === 'referral' &&
		cases.referralCase &&
		!cases.referralCase.canOpen
	) {
		if (
			cases.referralCase.qualifiedReferrals >=
			cases.referralCase.requiredReferrals
		) {
			return t('outOfStock')
		}

		return t('referrals', [
			cases.referralCase.qualifiedReferrals,
			cases.referralCase.requiredReferrals
		])
	}

	return slots.actionLeft ? t('open') : t('free')
})

const openWelcomeText = computed(() => {
	if (!cases.welcomeCase?.count) {
		return t('outOfStock')
	}

	return slots.actionLeft ? t('open') : t('free')
})

const fairness = ref(false)

const openFairness = (dropped?: boolean) => {
	if (dropped) {
		if (!droppedItems.value.length) {
			return
		}

		const { serverSeedHash, serverSeed, clientSeed, ticketsCount } =
			droppedItems.value[0].fair

		fair.value = {
			nonce: droppedItems.value.map((item) => item.fair.nonce),
			serverSeedHash,
			serverSeed,
			clientSeed,
			ticketsCount,
			ticket: droppedItems.value.map((item) => item.fair.ticket)
		}
	} else {
		fair.value = initialFair.value
	}

	fairness.value = true
}

const initialFair = computed(() => ({
	nonce: Array.from(
		{ length: count.value },
		(_, i) => user.seedPairGames + i + 1
	),
	serverSeedHash: user.serverSeedHash,
	serverSeed: null,
	clientSeed: user.clientSeed,
	ticketsCount:
		items.value.reduce((acc, item) => Math.max(acc, item.ticketsRange[1]), 0) +
		1,
	ticket: null
}))

const fair = ref<{
	nonce: number[]
	serverSeedHash: string
	serverSeed: string | null
	clientSeed: string
	ticketsCount: number
	ticket: number[] | null
}>(initialFair.value)

onUnmounted(() => {
	if (!timeoutId) {
		return
	}

	clearTimeout(timeoutId)
})

const itemModal = ref(false)
const selectedItem = ref<{
	preloader: string | null
	collectionName: string
	modelName: string
	modelUuid: string
	price: number
	chance: number
	ticketsRange: [number, number]
}>()

const selectItem = async (item: {
	preloader: string | null
	collectionName: string
	modelName: string
	modelUuid: string
	price: number
	chance: number
	ticketsRange: [number, number]
}) => {
	selectedItem.value = item

	await nextTick()

	itemModal.value = true
}

const getItems = () => {
	return items.value
}

const sharing = ref({
	modelUuid: '',
	collectionName: '',
	modelName: '',
	chance: 0,
	price: 0
})
const shareLoading = ref(false)
const imageRender = ref<InstanceType<typeof RenderAsImage>>()

const share = async (uuid: string) => {
	if (shareLoading.value) {
		return
	}

	const item = droppedItems.value.find((i) => i.uuid === uuid)

	if (!item) {
		return
	}

	sharing.value = {
		modelUuid: item.modelUuid,
		collectionName: item.collection,
		modelName: item.name,
		price: item.price,
		chance: item.chance
	}

	if (!imageRender.value) {
		return
	}

	shareLoading.value = true

	const blob = await imageRender.value.renderAsImage()

	if (!blob) {
		shareLoading.value = false
		toast.add({ text: t('shareFailed'), type: 'error' })
		return
	}

	const file = new File([blob], 'story.png', {
		type: blob.type,
		lastModified: Date.now()
	})

	const { data, error } = await client.user.uploadStoryPhoto({
		file
	})

	if (error || !data) {
		console.error(error)
		toast.add({ text: t('shareFailed'), type: 'error' })
		shareLoading.value = false
		return
	}

	shareLoading.value = false

	WebApp.shareToStory(data.url, {
		text: '@ragegift',
		widget_link: {
			url: `https://t.me/ragegiftbot/app?startapp=r-${user.referralCode.toUpperCase()}`,
			name: t('claim')
		}
	})
}

defineExpose({
	spin,
	openFairness: () => openFairness(),
	getItems
})
</script>
